import React from 'react';
import type { Key } from 'react';
import { Checkbox, Field, Select } from '@milanote/roma';
import type { CheckboxProps } from '@milanote/roma';

import {
    DateStringFormatOptions,
    FormatOptions,
    TimeStringFormatOptions,
} from '../../../../common/table/CellTypeConstants';
import { getFormattedDate, getFormattedTime } from '../../../../common/table/utils/tableCellFormattingUtils';
import type { CellTypeFormatChanges, DateTimeTypeObject } from '../../../../common/table/TableTypes';
import { MILANOTE_BIRTHDAY_DATE } from '../../../../common/utils/datetime/dateUtils';

import { useTranslator } from '../../../components/i18n/useTranslator';

type Props = {
    cellTypeObject: DateTimeTypeObject;
    updateCellTypeFormat: (cellTypeFormatChanges: CellTypeFormatChanges) => void;
    locale: string;
    formSize: CheckboxProps['size'];
};

const DateFormatOptions: React.FC<Props> = ({ cellTypeObject, updateCellTypeFormat, locale, formSize }) => {
    const { dateFormat, timeFormat, timeInBrackets } = cellTypeObject;
    const translate = useTranslator('popup', 'table.format');

    const timeOnlyDisplayed = dateFormat === DateStringFormatOptions.NONE;
    const dateOnlyDisplayed = timeFormat === TimeStringFormatOptions.NONE;

    const dateFormatItems = Object.values(DateStringFormatOptions).map((option) => ({
        id: option,
        label: getFormattedDate(MILANOTE_BIRTHDAY_DATE, option, locale) || translate('dateNone'),
        isDisabled: option === DateStringFormatOptions.NONE && dateOnlyDisplayed,
    }));

    const timeFormatItems = Object.values(TimeStringFormatOptions).map((option) => ({
        id: option,
        label: getFormattedTime(MILANOTE_BIRTHDAY_DATE, option, locale) || translate('timeNone'),
        isDisabled: option === TimeStringFormatOptions.NONE && timeOnlyDisplayed,
    }));

    const updateDateFormat = (key: Key | null): void => {
        if (key == null) return;

        updateCellTypeFormat({ [FormatOptions.DATE_FORMAT]: String(key) as DateStringFormatOptions });
    };

    const updateTimeFormat = (key: Key | null): void => {
        if (key == null) return;

        const timeFormatValue = String(key) as TimeStringFormatOptions;
        updateCellTypeFormat({
            [FormatOptions.TIME_FORMAT]: timeFormatValue,
            ...(timeFormatValue !== TimeStringFormatOptions.NONE && {
                [FormatOptions.HAS_TIME]: true,
            }),
        });
    };

    return (
        <>
            <div className="DateFormatOptions celltype-format-section">
                <Field.Label className="celltype-format-title" size={formSize}>
                    {translate('dateFormat')}
                </Field.Label>
                <div className="celltype-format-inputs">
                    <div className="celltype-format-input">
                        <Select size={formSize} value={dateFormat} onChange={updateDateFormat} items={dateFormatItems}>
                            {(item) => <Select.Item {...item} />}
                        </Select>
                    </div>
                </div>
            </div>
            <div className="DateFormatOptions celltype-format-section">
                <Field.Label className="celltype-format-title" size={formSize}>
                    {translate('timeFormat')}
                </Field.Label>
                <div className="celltype-format-inputs">
                    <div className="celltype-format-input">
                        <Select size={formSize} value={timeFormat} onChange={updateTimeFormat} items={timeFormatItems}>
                            {(item) => <Select.Item {...item} />}
                        </Select>
                    </div>
                    {timeFormat !== TimeStringFormatOptions.NONE && (
                        <div className="checkbox-input celltype-format-input">
                            <Checkbox
                                label={translate('timeInBrackets')}
                                size={formSize}
                                variant="primary"
                                isSelected={timeInBrackets}
                                onChange={(nextChecked) =>
                                    updateCellTypeFormat({ [FormatOptions.TIME_IN_BRACKETS]: nextChecked })
                                }
                            />
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default DateFormatOptions;
